import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Share2, Mail, ArrowLeft, Crown, Download, Loader2, Eye, Send, Copy, Check, X, Tag, Lock } from 'lucide-react';
import { archetypeDescriptions, visualAttributeQuestions } from '../data/mock';
import { getResultContent, getArchetypeCard, getArchetypeExamples } from '../data/archetypeResultContent';
import { toast } from 'sonner';
import { useRazorpay } from 'react-razorpay';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { analytics } from '../utils/analytics';
import { TestimonialModal, shouldShowTestimonial } from '../components/TestimonialModal';
import { ResultsMainContent } from '../components/results/ResultsMainContent';

// Map archetype names to their PNG icon paths (lowercase for file names)
const archetypeIconPaths = {
  "Innocent": "/icons/innocent.png",
  "Explorer": "/icons/explorer.png",
  "Sage": "/icons/sage.png",
  "Hero": "/icons/hero.png",
  "Rebel": "/icons/rebel.png",
  "Magician": "/icons/magician.png",
  "Everyman": "/icons/everyman.png",
  "Lover": "/icons/lover.png",
  "Jester": "/icons/jester.png",
  "Caregiver": "/icons/caregiver.png",
  "Ruler": "/icons/ruler.png",
  "Creator": "/icons/creator.png"
};

const getArchetypeIconPath = (archetypeName) => {
  return archetypeIconPaths[archetypeName] || "/icons/everyman.png";
};

// Pricing fallbacks — overridden by /api/report-price once it loads
const DEFAULT_REPORT_PRICE_INR = 2499;
const DEFAULT_REPORT_PRICE_PAISE = DEFAULT_REPORT_PRICE_INR * 100; // Razorpay accepts paise (₹1 = 100 paise)

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { results, email, name, companyName, visualAnswers, visualVibes: backendVibes, submissionId } = location.state || {};
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isPreviewingPDF, setIsPreviewingPDF] = useState(false);
  const [isEmailingReport, setIsEmailingReport] = useState(false);
  const [lastReportId, setLastReportId] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showReportSuccessModal, setShowReportSuccessModal] = useState(false);
  const [reportModalPhase, setReportModalPhase] = useState('generating'); // 'generating' | 'complete'
  const [generationProgress, setGenerationProgress] = useState(0);
  // Retained payload for the last generation attempt so we can offer a Retry button on failure
  const [lastGenPayload, setLastGenPayload] = useState(null);
  const { Razorpay } = useRazorpay();

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {discount, final_amount, coupon_value, coupon_type, message}
  const [reportPrice, setReportPrice] = useState({ paise: DEFAULT_REPORT_PRICE_PAISE, inr: DEFAULT_REPORT_PRICE_INR, basePaise: null, baseInr: null });
  // Testimonial modal — fires 6s after results render AND once after PDF download
  const [showTestimonial, setShowTestimonial] = useState(false);
  const [testimonialSource, setTestimonialSource] = useState('results_page');

  // 6-second post-results-load trigger (skip if user already submitted)
  useEffect(() => {
    if (!shouldShowTestimonial()) return;
    const t = setTimeout(() => {
      setTestimonialSource('results_page');
      setShowTestimonial(true);
    }, 6000);
    return () => clearTimeout(t);
  }, []);
  const ORIGINAL_AMOUNT = reportPrice.paise;

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/report-price`)
      .then(r => r.json())
      .then(d => {
        if (d.price_paise) setReportPrice({
          paise: d.price_paise,
          inr: d.price_inr,
          basePaise: d.base_price_paise ?? null,
          baseInr: d.base_price_inr ?? null,
        });
      })
      .catch(() => {});
  }, []);

  // Animate progress bar while generating
  useEffect(() => {
    if (!showReportSuccessModal || reportModalPhase !== 'generating') {
      setGenerationProgress(0);
      return;
    }
    let frame;
    const start = Date.now();
    const duration = 55000; // 55 seconds to reach ~90%
    const tick = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min(90, (elapsed / duration) * 90);
      setGenerationProgress(pct);
      if (pct < 90) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [showReportSuccessModal, reportModalPhase]);

  // ── Durable job resume: if a previous session left an in-flight job in
  //    localStorage, pick it back up automatically. Runs once on mount. ──
  useEffect(() => {
    if (!results) return;
    const REPORT_JOB_KEY = 'brandquiz.activeReportJob';
    let raw;
    try { raw = localStorage.getItem(REPORT_JOB_KEY); } catch { return; }
    if (!raw) return;
    let stored;
    try { stored = JSON.parse(raw); } catch { return; }
    if (!stored?.jobId) return;
    // Drop stale (>30 min) records — beyond retry window
    if (Date.now() - (stored.savedAt || 0) > 30 * 60 * 1000) {
      try { localStorage.removeItem(REPORT_JOB_KEY); } catch { /* noop */ }
      return;
    }
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    (async () => {
      try {
        setIsGeneratingPDF(true);
        setReportModalPhase('generating');
        setGenerationProgress(0);
        setShowReportSuccessModal(true);
        const statusRes = await fetch(`${API_URL}/api/report-status/${stored.jobId}`);
        if (statusRes.status === 404) {
          try { localStorage.removeItem(REPORT_JOB_KEY); } catch { /* noop */ }
          setShowReportSuccessModal(false);
          return;
        }
        const status = await statusRes.json();
        if (status.status === 'complete') {
          if (status.report_id) setLastReportId(status.report_id);
          setGenerationProgress(100);
          setReportModalPhase('complete');
          try { localStorage.removeItem(REPORT_JOB_KEY); } catch { /* noop */ }
          return;
        }
        if (status.status === 'failed') {
          setShowReportSuccessModal(false);
          setLastGenPayload(stored.payload || null);
          toast.error('Your last report attempt failed. Tap Retry to try again.');
          return;
        }
        const result = await pollReportJob(stored.jobId, (upd) => {
          if (upd.phase === 'pdf-ready') {
            setLastReportId(upd.report_id);
            toast.success('Your report is ready — you can download it now.', {
              duration: 5000, id: 'pdf-ready-toast',
            });
          }
        });
        if (result.report_id) setLastReportId(result.report_id);
        setGenerationProgress(100);
        setReportModalPhase('complete');
        try { localStorage.removeItem(REPORT_JOB_KEY); } catch { /* noop */ }
      } catch (err) {
        // Recovery path: if the user actually already has a fresh report,
        // don't nag them with a retry banner — just surface the download.
        const savedEmail = stored?.payload?.body?.user_data?.email;
        if (savedEmail) {
          try {
            const rr = await fetch(
              `${API_URL}/api/recent-report?email=${encodeURIComponent(savedEmail)}&minutes=60`
            );
            if (rr.ok) {
              const recent = await rr.json();
              if (recent?.report_id) {
                setLastReportId(recent.report_id);
                try { localStorage.removeItem(REPORT_JOB_KEY); } catch { /* noop */ }
                setShowReportSuccessModal(false);
                toast.success('Welcome back — your report is ready to download.', {
                  duration: 5000, id: 'recent-report-toast',
                });
                return;
              }
            }
          } catch { /* fall through */ }
        }
        setShowReportSuccessModal(false);
        setLastGenPayload(stored.payload || null);
        toast.error(err.message || 'Could not resume your report. Tap Retry to try again.');
      } finally {
        setIsGeneratingPDF(false);
      }
    })();
  }, []);

  if (!results) {
    navigate('/');
    return null;
  }

  const dominant = archetypeDescriptions[results.dominant];
  const secondary = archetypeDescriptions[results.secondary];
  const dominantIconPath = getArchetypeIconPath(results.dominant);
  const secondaryIconPath = getArchetypeIconPath(results.secondary);

  // Get visual vibe preferences from Section 3 answers
  const getVisualVibePreferences = () => {
    if (!visualAnswers || visualAnswers.length === 0) return [];
    return visualAnswers.map(answer => {
      const question = visualAttributeQuestions.find(q => q.id === answer.questionId);
      return answer.answer === 'option1' ? question.option1 : question.option2;
    });
  };

  const visualVibes = backendVibes || getVisualVibePreferences();

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/share-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          primary_archetype: results.dominant,
          secondary_archetype: results.secondary,
          visual_vibes: visualVibes || [],
          all_scores: results.allScores || [],
          archetype_data: { primary: dominant, secondary: secondary },
          company_label: companyName ? `${companyName}'s Brand` : null
        })
      });
      const data = await response.json();
      const url = `${window.location.origin}/share/${data.share_id}`;
      setShareUrl(url);
      setShowShareModal(true);
    } catch {
      toast.error('Could not generate share link. Please try again.');
    } finally {
      setIsSharing(false);
    }
  };

  const handleCopyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
    toast.success('Link copied!');
  };

  const handleEmailResults = async () => {
    if (!email) {
      toast.error('Please enter your email address first.');
      return;
    }
    const toastId = toast.loading('Sending your results to your email...');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/email-results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_data: { name, email, company_name: companyName },
          quiz_results: results,
          archetype_data: {
            primary: dominant,
            secondary: secondary
          },
          visual_vibes: visualVibes || [],
          all_scores: results.allScores || []
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || 'Results sent! Check your inbox.', { id: toastId });
      } else {
        toast.error(data.message || 'Failed to send. Please try again.', { id: toastId });
      }
    } catch (err) {
      toast.error('Failed to send email. Please try again.', { id: toastId });
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    setIsApplyingCoupon(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/apply-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode.trim(),
          order_amount: ORIGINAL_AMOUNT,
          user_email: email || '',
        })
      });
      const data = await response.json();
      if (data.success) {
        setAppliedCoupon(data);
        toast.success(data.message);
      } else {
        toast.error(data.message || 'Invalid coupon');
        setAppliedCoupon(null);
      }
    } catch {
      toast.error('Failed to apply coupon. Try again.');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  // Poll a background report generation job until complete/failed
  const pollReportJob = async (jobId, onProgress) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const maxAttempts = 240; // 240 × 2s = 8 minutes max
    let sawReportId = false;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await fetch(`${API_URL}/api/report-status/${jobId}`);
        if (res.status === 404) throw new Error('Report job not found');
        const job = await res.json();
        // As soon as the PDF is ready, surface report_id so the UI can enable download
        if (job.report_id && !sawReportId) {
          sawReportId = true;
          onProgress && onProgress({ phase: 'pdf-ready', report_id: job.report_id });
        }
        if (job.status === 'complete') return job;
        if (job.status === 'failed') throw new Error(job.error || 'Report generation failed');
      } catch (err) {
        // network blip — keep polling; propagate only real failures
        if (err.message && !err.message.includes('Failed to fetch') && !err.message.includes('NetworkError')) {
          throw err;
        }
      }
    }
    throw new Error('Report generation timed out');
  };

  // ── Durable job recovery: persist job_id in localStorage so a page
  //    refresh doesn't force the user to retake the quiz ───────────────
  const REPORT_JOB_KEY = 'brandquiz.activeReportJob';
  const saveActiveJob = (jobId, payload) => {
    try {
      localStorage.setItem(REPORT_JOB_KEY, JSON.stringify({
        jobId, payload, savedAt: Date.now(),
      }));
    } catch { /* storage may be full or blocked */ }
  };
  const clearActiveJob = () => {
    try { localStorage.removeItem(REPORT_JOB_KEY); } catch { /* noop */ }
  };

  const handleClaimFreeReport = async () => {
    setIsProcessingPayment(true);
    setIsGeneratingPDF(true);
    setReportModalPhase('generating');
    setGenerationProgress(0);
    setShowReportSuccessModal(true);
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const payload = {
      kind: 'free',
      body: {
        user_data: {
          name: name || 'Brand Owner',
          company_name: companyName || 'Your Brand',
          email: email || '',
        },
        quiz_results: { ...results, submission_id: submissionId },
        visual_vibes: visualVibes,
        coupon_code: couponCode.trim(),
      },
    };
    setLastGenPayload(payload);
    try {
      // Step 1: Start generation (returns instantly)
      const startRes = await fetch(`${API_URL}/api/start-report-generation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload.body)
      });
      const startData = await startRes.json();
      if (!startRes.ok) throw new Error(startData.detail || 'Failed to start report');

      // Persist the job so a refresh can resume it
      saveActiveJob(startData.job_id, payload);

      // Step 2: Poll until complete
      const result = await pollReportJob(startData.job_id, (upd) => {
        if (upd.phase === 'pdf-ready') {
          setLastReportId(upd.report_id);
          toast.success('Your report is ready — you can download it now.', {
            duration: 5000, id: 'pdf-ready-toast',
          });
        }
      });
      if (result.report_id) setLastReportId(result.report_id);
      setGenerationProgress(100);
      setReportModalPhase('complete');
      clearActiveJob();
      analytics.reportGenerate('free', results?.dominant, results?.secondary);
    } catch (error) {
      console.error('Free report error:', error);
      setShowReportSuccessModal(false);
      toast.error(error.message || 'Failed to generate report. Tap "Retry Report" to try again.');
    } finally {
      setIsProcessingPayment(false);
      setIsGeneratingPDF(false);
    }
  };

  // Re-run the last generation attempt without asking the user to redo anything.
  const handleRetryReport = async () => {
    if (!lastGenPayload) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;

    // Retake guard: if the user already has a completed report in the last hour,
    // skip regenerating and just surface the existing download.
    const userEmail = lastGenPayload?.body?.user_data?.email;
    if (userEmail) {
      try {
        const rr = await fetch(
          `${API_URL}/api/recent-report?email=${encodeURIComponent(userEmail)}&minutes=60`
        );
        if (rr.ok) {
          const recent = await rr.json();
          if (recent?.report_id) {
            setLastReportId(recent.report_id);
            setLastGenPayload(null);
            toast.success('Good news — your recent report is still available.', {
              duration: 5000, id: 'recent-report-toast',
            });
            return;
          }
        }
      } catch { /* fall through to normal retry */ }
    }

    setIsProcessingPayment(true);
    setIsGeneratingPDF(true);
    setReportModalPhase('generating');
    setGenerationProgress(0);
    setShowReportSuccessModal(true);
    try {
      const startRes = await fetch(`${API_URL}/api/start-report-generation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lastGenPayload.body)
      });
      const startData = await startRes.json();
      if (!startRes.ok) throw new Error(startData.detail || 'Failed to start report');
      saveActiveJob(startData.job_id, lastGenPayload);
      const result = await pollReportJob(startData.job_id, (upd) => {
        if (upd.phase === 'pdf-ready') {
          setLastReportId(upd.report_id);
          toast.success('Your report is ready — you can download it now.', {
            duration: 5000, id: 'pdf-ready-toast',
          });
        }
      });
      if (result.report_id) setLastReportId(result.report_id);
      setGenerationProgress(100);
      setReportModalPhase('complete');
      clearActiveJob();
    } catch (error) {
      setShowReportSuccessModal(false);
      toast.error(error.message || 'Retry failed. Please try once more or contact support.');
    } finally {
      setIsProcessingPayment(false);
      setIsGeneratingPDF(false);
    }
  };

  const handlePremiumPurchase = async () => {
    // If coupon makes it free, skip Razorpay entirely
    if (appliedCoupon && appliedCoupon.final_amount === 0) {
      return handleClaimFreeReport();
    }

    setIsProcessingPayment(true);
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const payAmount = appliedCoupon ? appliedCoupon.final_amount : ORIGINAL_AMOUNT;

      // Step 1: Create order on backend
      const orderResponse = await fetch(`${API_URL}/api/create-payment-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: payAmount,
          currency: 'INR',
          receipt: `quiz_${Date.now()}`,
          quiz_data: {
            results,
            visualAnswers,
            user: { name, email, companyName }
          },
          coupon_code: appliedCoupon ? couponCode.trim() : null
        })
      });
      
      if (!orderResponse.ok) {
        throw new Error('Failed to create payment order');
      }
      
      const orderData = await orderResponse.json();
      
      // Step 2: Open Razorpay checkout
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Therefore Design',
        description: 'Premium Brand Strategy Guide',
        order_id: orderData.id,
        handler: async function (response) {
          // Step 3: Verify payment on backend
          try {
            setIsGeneratingPDF(true);
            // Show progress modal immediately after payment
            setReportModalPhase('generating');
            setGenerationProgress(0);
            setShowReportSuccessModal(true);
            
            const verifyResponse = await fetch(`${API_URL}/api/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                user_data: { name, email, companyName },
                quiz_data: { results, visualAnswers }
              })
            });
            
            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }
            
            // Step 4: Start background report generation (returns instantly)
            const premiumPayload = {
              kind: 'premium',
              body: {
                user_data: {
                  name: name || 'Brand Owner',
                  company_name: companyName || 'Your Brand',
                  email: email || ''
                },
                quiz_results: results,
                visual_vibes: visualVibes,
                payment_id: response.razorpay_payment_id
              },
            };
            setLastGenPayload(premiumPayload);
            const startRes = await fetch(`${API_URL}/api/start-report-generation`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(premiumPayload.body)
            });
            const startData = await startRes.json();
            if (!startRes.ok) throw new Error(startData.detail || 'Failed to start report');
            saveActiveJob(startData.job_id, premiumPayload);

            // Step 5: Poll until complete
            const result = await pollReportJob(startData.job_id, (upd) => {
              if (upd.phase === 'pdf-ready') {
                setLastReportId(upd.report_id);
                toast.success('Your report is ready — you can download it now.', {
                  duration: 5000, id: 'pdf-ready-toast',
                });
              }
            });
            if (result.report_id) setLastReportId(result.report_id);
            setGenerationProgress(100);
            setReportModalPhase('complete');
            clearActiveJob();
            analytics.reportGenerate('premium', results?.dominant, results?.secondary);
            
          } catch (error) {
            console.error('Post-payment error:', error);
            setShowReportSuccessModal(false);
            toast.error('Payment received. Report generation hit a snag — tap "Retry Report" or contact support with your payment id.');
          } finally {
            setIsGeneratingPDF(false);
          }
        },
        prefill: {
          name: name || '',
          email: email || '',
          contact: ''
        },
        theme: {
          color: '#7A00EB'
        },
        modal: {
          ondismiss: function() {
            setIsProcessingPayment(false);
            toast.info('Payment cancelled');
          }
        }
      };
      
      const razorpayInstance = new Razorpay(options);
      razorpayInstance.on('payment.failed', function (response) {
        toast.error(`Payment failed: ${response.error.description}`);
        setIsProcessingPayment(false);
      });
      razorpayInstance.open();
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  const handleDownloadSample = () => {
    // Use the static downloadable sample PDF
    const link = document.createElement('a');
    link.href = '/Therefore_Brand_Archetype_Sample_Report.pdf';
    link.download = 'Brand_Personality_Blueprint_Sample.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Sample report downloading! See what the full version includes.');
  };

  // Preview full report (bypasses payment for testing)
  const handlePreviewFullReport = async () => {
    setIsPreviewingPDF(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/preview-premium-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_data: { 
            name: name || 'Brand Owner', 
            company_name: companyName || 'Your Brand',
            email: email || '' 
          },
          quiz_results: results,
          visual_vibes: visualVibes
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate preview report');
      }
      
      // Get report ID from header
      const reportId = response.headers.get('X-Report-ID');
      if (reportId) {
        setLastReportId(reportId);
      }
      
      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PREVIEW_Brand_Strategy_Guide_${(companyName || 'Brand').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Preview report downloaded! This is the full 16-page report.');
    } catch (error) {
      console.error('Error downloading preview:', error);
      toast.error('Failed to download preview. Please try again.');
    } finally {
      setIsPreviewingPDF(false);
    }
  };

  // Email report to user
  const handleEmailReport = async (reportId = null) => {
    const idToEmail = reportId || lastReportId;
    
    if (!idToEmail) {
      toast.error('Please download a report first, then you can email it.');
      return;
    }
    
    if (!email) {
      toast.error('No email address available.');
      return;
    }
    
    setIsEmailingReport(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/email-report/${idToEmail}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Report sent to ${email}!`);
      } else {
        toast.error(data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error emailing report:', error);
      toast.error('Failed to send email. Please try again.');
    } finally {
      setIsEmailingReport(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-black">
      <Header />
      
      <ResultsMainContent
        results={results}
        name={name}
        companyName={companyName}
        email={email}
        reportPrice={reportPrice}
        dominant={dominant}
        handleEmailResults={handleEmailResults}
        handleShare={handleShare}
        isSharing={isSharing}
        handlePremiumPurchase={handlePremiumPurchase}
        handleDownloadSample={handleDownloadSample}
        isProcessingPayment={isProcessingPayment}
        isGeneratingPDF={isGeneratingPDF}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        appliedCoupon={appliedCoupon}
        isApplyingCoupon={isApplyingCoupon}
        handleApplyCoupon={handleApplyCoupon}
        handleRemoveCoupon={handleRemoveCoupon}
        lastReportId={lastReportId}
        handleEmailReport={handleEmailReport}
        isEmailingReport={isEmailingReport}
      />

      {/* Retry banner — visible when the last report attempt failed and the modal is dismissed */}
      {lastGenPayload && !showReportSuccessModal && !lastReportId && (
        <div
          className="fixed bottom-6 right-6 z-40 max-w-sm bg-[#1a1a1a] border border-[#ec008c]/40 rounded-lg shadow-2xl p-4"
          data-testid="report-retry-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#ec008c]/20 flex items-center justify-center flex-shrink-0">
              <Loader2 className="h-4 w-4 text-[#ec008c]" />
            </div>
            <div className="flex-1">
              <div className="text-white font-semibold text-sm">
                Report generation didn&apos;t finish
              </div>
              <div className="text-white/60 text-xs mt-1 mb-3">
                Your quiz results are safe — we&apos;ll pick up where things left off.
              </div>
              <Button
                onClick={handleRetryReport}
                disabled={isProcessingPayment || isGeneratingPDF}
                className="bg-[#ec008c] hover:bg-[#ec008c]/90 text-white font-bold text-xs py-2 px-4 h-auto"
                data-testid="retry-report-btn"
              >
                Retry Report
              </Button>
            </div>
            <button
              onClick={() => setLastGenPayload(null)}
              className="text-white/40 hover:text-white/70 -mt-1 -mr-1"
              aria-label="Dismiss"
              data-testid="dismiss-retry-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && shareUrl && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setShowShareModal(false)}
          data-testid="share-modal"
        >
          <div
            className="bg-[#0a0a0a] w-full max-w-md p-0 overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="bg-black px-6 py-5 flex items-center justify-between">
              <div>
                <div className="text-white font-extrabold text-lg tracking-tight">Share Your Results</div>
                <div className="text-gray-400 text-xs mt-0.5">Let your network discover their brand archetype</div>
              </div>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-[#9ca3af] hover:text-white transition-colors"
                data-testid="close-share-modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              {/* Archetype preview */}
              <div className="bg-[#111] border border-[#262626] px-4 py-3 mb-5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#9ca3af] font-medium uppercase tracking-wider mb-0.5">Your Brand Archetype</div>
                  <div className="font-extrabold text-[#f5f5f5] text-base truncate">{results.dominant}</div>
                  {results.secondary && (
                    <div className="text-xs text-[#9ca3af] mt-0.5">+ {results.secondary}</div>
                  )}
                </div>
                <Share2 className="h-5 w-5 text-gray-400 shrink-0" />
              </div>

              {/* Copy link */}
              <div className="flex gap-2 mb-5">
                <div className="flex-1 bg-[#1a1a1a] border border-[#262626] px-3 py-2.5 text-xs text-[#a3a3a3] font-mono truncate rounded-none">
                  {shareUrl}
                </div>
                <button
                  onClick={handleCopyLink}
                  className="bg-black text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors flex items-center gap-1.5 shrink-0"
                  data-testid="copy-share-link-btn"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              {/* Social share buttons */}
              <div className="text-xs text-[#9ca3af] font-medium uppercase tracking-wider mb-3">Share on</div>
              <div className="grid grid-cols-3 gap-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Discovered my brand archetype: ${results.dominant}! Take the quiz yourself 👇 ${shareUrl}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 border-2 border-[#262626] hover:border-[#e5e5e5] hover:bg-gray-900 hover:text-white py-3 px-2 transition-colors group"
                  data-testid="share-whatsapp-btn"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  <span className="text-xs font-semibold">WhatsApp</span>
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 border-2 border-[#262626] hover:border-[#e5e5e5] hover:bg-gray-900 hover:text-white py-3 px-2 transition-colors"
                  data-testid="share-linkedin-btn"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  <span className="text-xs font-semibold">LinkedIn</span>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Just discovered my brand archetype: ${results.dominant}! What's yours? 🧠`)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 border-2 border-[#262626] hover:border-[#e5e5e5] hover:bg-gray-900 hover:text-white py-3 px-2 transition-colors"
                  data-testid="share-twitter-btn"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  <span className="text-xs font-semibold">X / Twitter</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Generation / Success Modal */}
      <Dialog open={showReportSuccessModal} onOpenChange={(open) => {
        if (!open && reportModalPhase === 'complete') setShowReportSuccessModal(false);
      }}>
        <DialogContent
          className="sm:max-w-md border-0 overflow-hidden p-0 [&>button]:text-white [&>button]:hover:text-white/80"
          style={{ backgroundImage: 'url(/td_gradient.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}
          onPointerDownOutside={(e) => { if (reportModalPhase === 'generating') e.preventDefault(); }}
        >
          {reportModalPhase === 'generating' ? (
            <div className="px-6 py-8 flex flex-col items-center text-center" data-testid="report-generating-phase">
              {/* Archetype animation */}
              <img
                src="/archetype_animation.gif"
                alt="Generating"
                className="w-28 h-28 object-contain mb-5"
              />

              <h2 className="text-xl font-bold text-white mb-3">
                Generating Your Report
              </h2>

              <p className="text-white/80 text-sm leading-relaxed mb-6 max-w-xs">
                Hold tight! We&apos;re turning your answers into a shiny new brand personality report. Our digital elves need about a minute.
              </p>

              {/* Progress bar */}
              <div className="w-full bg-[#0a0a0a]/20 rounded-full h-2.5 overflow-hidden backdrop-blur-sm mb-2">
                <div
                  className="h-full rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: `${generationProgress}%`,
                    background: 'linear-gradient(90deg, #ec008c, #00B5B4)',
                  }}
                  data-testid="report-progress-bar"
                />
              </div>
              <p className="text-xs text-white/50">
                {generationProgress < 30 ? 'Analysing your responses...' :
                 generationProgress < 60 ? 'Crafting your brand blueprint...' :
                 generationProgress < 85 ? 'Adding finishing touches...' :
                 'Almost there...'}
              </p>
            </div>
          ) : (
            <div className="px-6 py-8 flex flex-col items-center text-center" data-testid="report-complete-phase">
              {/* Success check */}
              <div className="w-16 h-16 rounded-full bg-[#0a0a0a]/10 backdrop-blur-sm flex items-center justify-center mb-4 border border-white/20">
                <Check className="h-8 w-8 text-emerald-400" />
              </div>

              <h2 className="text-xl font-bold text-white mb-3">
                Your Report Has Been Sent!
              </h2>

              <div className="bg-[#0a0a0a]/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 mb-4 w-full">
                <p className="text-white/90 text-sm">
                  Your report has been mailed to you on your email id.
                  <strong className="block mt-2 text-white">Please check your Spam folder.</strong>
                </p>
              </div>

              <p className="text-xs text-white/50 mb-5">
                In case the mail has not been received, please shoot a mail to{' '}
                <a href="mailto:info@thereforedesign.co.in" className="text-cyan-400 hover:underline">
                  info@thereforedesign.co.in
                </a>
                {' '}and we would be glad to resend the same.
              </p>

              {lastReportId && (
                <Button
                  onClick={() => {
                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                    window.open(`${API_URL}/api/admin/reports/${lastReportId}/download`, '_blank');
                    // Trigger testimonial modal once, 8s after download click
                    if (shouldShowTestimonial()) {
                      setTimeout(() => {
                        setTestimonialSource('post_pdf');
                        setShowTestimonial(true);
                      }, 8000);
                    }
                  }}
                  className="w-full bg-[#0a0a0a] text-[#f5f5f5] hover:bg-[#0a0a0a]/90 font-bold py-3 mb-3"
                  data-testid="download-report-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Here
                </Button>
              )}

              <Button
                variant="outline"
                onClick={() => setShowReportSuccessModal(false)}
                className="w-full border-white/30 text-white hover:bg-[#0a0a0a]/10 hover:text-white"
                data-testid="close-report-modal-btn"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Testimonial collection modal — fires 6s post-load & 8s post PDF download */}
      <TestimonialModal open={showTestimonial} onOpenChange={setShowTestimonial} source={testimonialSource} />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Results;
